import { Card, CardContent } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent } from "@/components/ui/tabs"
import { TooltipProvider } from "@/components/ui/tooltip"
import {
  AlertCircle, Activity, Users, MessageSquare, Flag,
  Zap, Layers, BarChart3, ClipboardList, LucideIcon
} from "lucide-react"
import { format } from "date-fns"
import { DashboardAssistant } from "@/lib/assistant/modules/dashboard"

import {
  OverviewTab, UsersTab, MessagesTab,
  EscalationsTab, VariablesTab, AIUsageTab, FormsTab
} from "./tabs"
import { DashboardControls } from "./shared/DashboardControls"
import { TabErrorBoundary } from "./shared/TabErrorBoundary"
import { ConfidenceBandBanner } from "./shared/ConfidenceBandBanner"
import { DASHBOARD_TABS, TabId } from "../utils/constants"
import { useDashboardState } from "../hooks/useDashboardState"

const TAB_ICONS: Record<string, LucideIcon> = { BarChart3, Users, MessageSquare, Flag, Layers, Zap, ClipboardList }

export function TelerivetDashboardSection() {
  const {
    team,
    error,
    messageDirectionFilter, setMessageDirectionFilter,
    handledByFilter, setHandledByFilter,
    escalationFilter, setEscalationFilter,
    contactSourceFilter, setContactSourceFilter,
    agentFilter, setAgentFilter,
    channelFilter, setChannelFilter, availableChannels,
    agents,
    loadedProjects, viewedProjects,
    dateRange, setDateRange, dateRangeLabel,
    contacts, allMessages,
    loading, loadingProgress,
    truncated, maxMessageRows,
    activeTab, setActiveTab,
    selectedVarKey, setSelectedVarKey,
    selectedVarValue, setSelectedVarValue,
    pinnedVarKeys, pinnedVarConfigs, togglePinnedVar, updatePinnedVarConfig,
    filteredContacts, filteredMessages,
    overviewAnalytics, dailyTrends, hourlyData: hourlyDistribution,
    flagBreakdown, resolvedTickets, resolvedContacts,
    activeTickets, activeContacts, totalEscalatedContacts,
    convosWithResolvedFlagContacts, needsReplyCount, needsReplyContacts,
    escalationRates, slaMetrics, ratingDistribution, correctedResponsesList, commentsList,
    statusBreakdown, allVarKeys, formFieldMeta, formRecordUpdatedAt,
    varValueBreakdown, contactsWithVarValue, pinnedBreakdowns,
    openaiAdminKey, setOpenaiAdminKey, openaiProjects,
    selectedOpenaiProject, setSelectedOpenaiProject,
    openaiUsage, handleFetchOpenAIUsage,
    businessHours,
    setBusinessHours,
  } = useDashboardState()

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-6">
      <DashboardControls
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        messageDirectionFilter={messageDirectionFilter}
        onMessageDirectionFilterChange={setMessageDirectionFilter}
        handledByFilter={handledByFilter}
        onHandledByFilterChange={setHandledByFilter}
        escalationFilter={escalationFilter}
        onEscalationFilterChange={setEscalationFilter}
        contactSourceFilter={contactSourceFilter}
        onContactSourceFilterChange={setContactSourceFilter}
        agents={agents}
        agentFilter={agentFilter}
        onAgentFilterChange={setAgentFilter}
        channels={availableChannels}
        channelFilter={channelFilter}
        onChannelFilterChange={setChannelFilter}
        loading={loading}
        loadingProgress={loadingProgress}
        error={error}
      />

      {truncated && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-900 [&>svg]:text-amber-600">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Showing the most recent <strong>{maxMessageRows.toLocaleString()}</strong> messages for this
            range — older messages were not loaded, so totals and breakdowns below are partial.
            Narrow the date range to see complete stats.
          </AlertDescription>
        </Alert>
      )}

      <ConfidenceBandBanner
        thumbsUpRate={overviewAnalytics.thumbsUpRate}
        ratingTotal={ratingDistribution.total}
        teamId={team?.id}
      />

      {contacts.length > 0 && allMessages.length > 0 && (
        <>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)} className="space-y-4">
          <Select value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)}>
            <SelectTrigger className="w-full max-w-xs">
              <SelectValue>
                {DASHBOARD_TABS.map(tab => {
                  if (tab.id !== activeTab) return null
                  const Icon = TAB_ICONS[tab.icon]
                  return <span key={tab.id} className="flex items-center gap-2"><Icon className="h-4 w-4" />{tab.label}</span>
                })}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {DASHBOARD_TABS.map(tab => {
                const Icon = TAB_ICONS[tab.icon]
                return (
                  <SelectItem key={tab.id} value={tab.id}>
                    <span className="flex items-center gap-2"><Icon className="h-4 w-4" />{tab.label}</span>
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>

          <TabsContent value="overview" className="space-y-6">
            <TabErrorBoundary tabName="Overview">
              {activeTab === "overview" && (
              <OverviewTab
                overviewAnalytics={overviewAnalytics}
                dailyTrends={dailyTrends}
                statusBreakdown={statusBreakdown}
                // Form fields have their own tab
                pinnedBreakdowns={pinnedBreakdowns.filter(p => !p.key.startsWith("form."))}
                onUnpinVar={togglePinnedVar}
                onNavigateToVariables={() => setActiveTab('variables')}
                ratingTotal={ratingDistribution.total}
                formFieldMeta={formFieldMeta}
                businessHours={businessHours}
                onBusinessHoursChange={setBusinessHours}
              />
              )}
            </TabErrorBoundary>
          </TabsContent>

          <TabsContent value="users" className="space-y-6">
            <TabErrorBoundary tabName="Users">
              <UsersTab contacts={contacts} filteredContacts={filteredContacts} overviewAnalytics={overviewAnalytics} dailyTrends={dailyTrends} />
            </TabErrorBoundary>
          </TabsContent>

          <TabsContent value="messages" className="space-y-6">
            <TabErrorBoundary tabName="Messages">
              <MessagesTab
                filteredMessages={filteredMessages}
                overviewAnalytics={overviewAnalytics}
                dailyTrends={dailyTrends}
                hourlyDistribution={hourlyDistribution}
                ratingDistribution={ratingDistribution}
                correctedResponsesList={correctedResponsesList}
                commentsList={commentsList}
                onOpenInHITL={(contact) => window.open(`/moderation?contact=${contact.id}`, '_blank')}
              />
            </TabErrorBoundary>
          </TabsContent>

          <TabsContent value="escalations" className="space-y-6">
            <TabErrorBoundary tabName="Escalations">
              <EscalationsTab
                flagBreakdown={flagBreakdown}
                resolvedTickets={resolvedTickets} resolvedContacts={resolvedContacts}
                activeTickets={activeTickets} activeContacts={activeContacts}
                totalEscalatedContacts={totalEscalatedContacts}
                convosWithResolvedFlagContacts={convosWithResolvedFlagContacts}
                needsReplyCount={needsReplyCount} needsReplyContacts={needsReplyContacts}
                escalationRates={escalationRates}
                slaMetrics={slaMetrics}
              />
            </TabErrorBoundary>
          </TabsContent>

          <TabsContent value="forms" className="space-y-6">
            <TabErrorBoundary tabName="Forms">
              {activeTab === "forms" && (
              <FormsTab
                filteredContacts={filteredContacts}
                formFieldMeta={formFieldMeta}
                formRecordUpdatedAt={formRecordUpdatedAt}
              />
              )}
            </TabErrorBoundary>
          </TabsContent>

          <TabsContent value="variables" className="space-y-6">
            <TabErrorBoundary tabName="Variables">
              <VariablesTab
                filteredContacts={filteredContacts}
                filteredMessages={filteredMessages}
                allVarKeys={allVarKeys}
                selectedVarKey={selectedVarKey}
                onSelectVarKey={setSelectedVarKey}
                varValueBreakdown={varValueBreakdown}
                selectedVarValue={selectedVarValue}
                onSelectVarValue={setSelectedVarValue}
                contactsWithVarValue={contactsWithVarValue}
                pinnedVarKeys={pinnedVarKeys}
                pinnedVarConfigs={pinnedVarConfigs}
                onTogglePin={togglePinnedVar}
                onUpdatePinnedConfig={updatePinnedVarConfig}
                formFieldMeta={formFieldMeta}
                onOpenInHITL={(contact) => window.open(`/moderation?contact=${contact.id}`, '_blank')}
              />
            </TabErrorBoundary>
          </TabsContent>

          <TabsContent value="ai-usage" className="space-y-6">
            <TabErrorBoundary tabName="Usage & Billing">
              <AIUsageTab
                openaiAdminKey={openaiAdminKey}
                onAdminKeyChange={setOpenaiAdminKey}
                openaiProjects={openaiProjects}
                selectedOpenaiProject={selectedOpenaiProject}
                onSelectedProjectChange={setSelectedOpenaiProject}
                openaiUsage={openaiUsage}
                onFetchUsage={handleFetchOpenAIUsage}
                dateRange={dateRange}
                filteredMessages={filteredMessages}
              />
            </TabErrorBoundary>
          </TabsContent>
          </Tabs>
        </>
      )}

      {(contacts.length === 0 || allMessages.length === 0) && !loading && (
        <Card className="border-2 border-dashed">
          <CardContent className="py-16 text-center">
            <Activity className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Data for Current Filters</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Adjust your date range or filters. Dashboard data now refreshes automatically from Supabase.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline">Auto-refresh enabled</Badge>
              <span>•</span>
              <span>{dateRange?.from && dateRange?.to ? `${format(dateRange.from, 'MMM d')} - ${format(dateRange.to, 'MMM d, yyyy')}` : 'No date range'}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {contacts.length > 0 && allMessages.length > 0 && loadedProjects.length > 0 && (
        <DashboardAssistant
          overviewAnalytics={overviewAnalytics}
          dailyTrends={dailyTrends}
          flagBreakdown={flagBreakdown}
          contacts={filteredContacts}
          messages={filteredMessages}
          commentsList={commentsList}
          correctedResponsesList={correctedResponsesList}
          dateRangeLabel={dateRangeLabel}
          projects={viewedProjects}
          allProjects={loadedProjects}
          allVarKeys={allVarKeys}
          pinnedVarKeys={pinnedVarKeys}
          pinnedVarConfigs={pinnedVarConfigs}
          onTogglePin={togglePinnedVar}
          onUpdatePinnedConfig={updatePinnedVarConfig}
          formFieldMeta={formFieldMeta}
        />
      )}
    </div>
    </TooltipProvider>
  )
}
